import React, { FC, useEffect, useState } from 'react';
import { ParsedQuery } from 'query-string';
import { useTranslation } from 'react-i18next';
import { useBlocker, useParams } from 'react-router-dom';
import {
  AddButton,
  ContentInNumbers,
  PageHeader,
  ParticipantsCont,
  ParticipantTileWrapper,
  SearchBoxCont,
  TotalItems,
} from './Participants.styles';
import { getLang } from '../../helpers/helpers';
import { useFilters } from '../../hooks/useFilters';
import { useParticipantsTeam } from '../../hooks/useParticipantsTeam';
import {
  IPagingInfo,
  IParticipantsItem,
  IParticipantsRes,
  ISurveyFilters,
} from '../../services/surveys';
import AddParticipantModal from '../AddParticipantModal/AddParticipantModal';
import DeleteParticipantModal from '../DeleteParticipantModal/DeleteParticipantModal';
import ImportParticipantModal from '../ImportParticipantModal/ImportParticipantModal';
import ParticipantsTable from '../ParticipantsTable/ParticipantsTable';
import SearchBox from '../SearchBox/SearchBox';
import { ConfirmSurveyQuitModal } from '../ConfirmSurveyQuitModal/ConfirmSurveyQuitModal';
import AddParticipantIcon from '@/assets/icons/addIcon.svg?react';

const metadataInitial = {
  pageNo: null,
  itemsPerPage: null,
  totalPageCount: null,
  totalItemsCount: null,
  memberCount: null,
  subordinateCount: null,
  superordinateCount: null,
  leaderCount: null,
};

interface IParticipants {
  baseLang: string;
  surveyType: string;
}

const Participants: FC<IParticipants> = ({ baseLang, surveyType }) => {
  const [currentEditData, setCurrentEditData] = useState<IParticipantsItem | undefined>(undefined);
  const [currentParticipantId, setCurrentParticipantId] = useState<string | null>(null);
  const [deleteModalActive, setDeleteModalActive] = useState<boolean>(false);
  const [importModalActive, setImportModalActive] = useState<boolean>(false);
  const [modalActive, setModalActive] = useState(false);
  const { t } = useTranslation();
  const [items, setItems] = useState<IParticipantsItem[]>([]);
  const [metadata, setMetadata] = useState<IPagingInfo>(metadataInitial as IPagingInfo);

  const { deleteParticipantsTeamMember, downloadParticipants, getAppliedFilters } =
    useParticipantsTeam();
  const { applyFilters, currentFilters } = useFilters();
  const { card, perPage, pageNo, search } = currentFilters;
  const { language } = getAppliedFilters();
  const params = useParams();
  const surveyId = params.surveyId || '1';
  const setParticipantsData = (res: IParticipantsRes) => {
    const { items, pagingInfo } = res;
    setItems(items);
    setMetadata(pagingInfo);
  };

  const isEffectSurvey = surveyType === 'EFFECT';

  const hasUnassignedParticipants =
    isEffectSurvey && items.length > 0 && items.some(item => !item.department);

  const blocker = useBlocker(hasUnassignedParticipants);

  const handleBlockerCancel = () => {
    blocker.reset?.();
  };

  const handleBlockerConfirm = () => {
    blocker.proceed?.();
  };

  const updateParticipantsList = async (filters: ParsedQuery = {}) => {
    const itemsList = await downloadParticipants(surveyId, filters);
    if (itemsList) {
      setParticipantsData(itemsList);
    }
  };

  const searchTable = (search: ISurveyFilters) => {
    applyFilters(search);
  };

  const handleModal = (status: boolean, data?: IParticipantsItem | undefined) => {
    const editData = data || undefined;
    if (editData) {
      setCurrentEditData(editData);
      return setModalActive(status);
    }
    setCurrentEditData(editData);
    return setModalActive(status);
  };

  const handleDeleteModal = (id: string | null) => {
    setCurrentParticipantId(id);
    setDeleteModalActive(activeModal => !activeModal);
  };

  const handleImportModal = (id: string | null) => {
    setCurrentParticipantId(id);
    setImportModalActive(activeModal => !activeModal);
  };

  const handleDeleteModalClose = () => {
    handleDeleteModal(null);
  };

  const handleImportModalClose = () => {
    handleImportModal(null);
  };

  const handleDeleteModalConfirm = async () => {
    if (!currentParticipantId) return;

    const pageNumber = items?.length !== 1 ? pageNo : '1';

    try {
      await deleteParticipantsTeamMember(surveyId, currentParticipantId);
      await updateParticipantsList({ pageNo: pageNumber });

      if (pageNumber !== pageNo) {
        applyFilters({ pageNo: pageNumber as string });
      }
    } finally {
      handleDeleteModal(null);
    }
  };

  useEffect(() => {
    if (blocker.state === 'blocked' && !hasUnassignedParticipants) {
      blocker.reset();
    }
  }, [blocker, hasUnassignedParticipants]);

  useEffect(() => {
    (async () => await updateParticipantsList())();
  }, [card, pageNo, perPage, search, language]);

  return (
    <ParticipantsCont id="ParticipantsCont" data-testid="ParticipantsContainer">
      <AddParticipantModal
        baseLang={baseLang}
        refreshList={updateParticipantsList}
        active={modalActive}
        closeModal={() => handleModal(false)}
        editData={currentEditData}
        surveyType={surveyType}
      />
      <DeleteParticipantModal
        active={deleteModalActive}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteModalConfirm}
      />
      <ImportParticipantModal
        active={importModalActive}
        onClose={handleImportModalClose}
        // onConfirm={handleImportModalConfirm}
      />
      <PageHeader id="TableHeadCont">
        {isEffectSurvey ? (
          <ContentInNumbers id="ContentInNumbers" data-testid="contentInNumbers">
            <ParticipantTileWrapper>
              {t('viewSurvey.participants.listHead.participants')}:{' '}
              <TotalItems id="TotalItems" data-testid="TotalItems">
                {metadata.totalItemsCount}
              </TotalItems>
            </ParticipantTileWrapper>

            <ParticipantTileWrapper>
              {t(
                `viewSurvey.reportsSetup.participants.additional.subordinate${
                  (metadata?.subordinateCount ?? 0) > 1 ? 's' : ''
                }`
              )}
              :{' '}
              <TotalItems id="TotalEmployees" data-testid="TotalEmployees">
                {metadata.subordinateCount}
              </TotalItems>
            </ParticipantTileWrapper>

            <ParticipantTileWrapper>
              {t(
                `viewSurvey.reportsSetup.participants.additional.member${
                  (metadata?.memberCount ?? 0) > 1 ? 's' : ''
                }`
              )}
              :{' '}
              <TotalItems id="TotalManagers" data-testid="TotalManagers">
                {metadata.memberCount}
              </TotalItems>
            </ParticipantTileWrapper>

            <ParticipantTileWrapper>
              {t(
                `viewSurvey.reportsSetup.participants.additional.leader${
                  (metadata?.leaderCount ?? 0) > 1 ? 's' : ''
                }`
              )}
              :{' '}
              <TotalItems id="TotalDirectors" data-testid="TotalDirectors">
                {metadata.leaderCount}
              </TotalItems>
            </ParticipantTileWrapper>

            <ParticipantTileWrapper>
              {t(
                `viewSurvey.reportsSetup.participants.additional.superordinate${
                  (metadata?.superordinateCount ?? 0) > 1 ? 's' : ''
                }`
              )}
              :{' '}
              <TotalItems id="TotalDirectors" data-testid="TotalDirectors">
                {metadata.superordinateCount}
              </TotalItems>
            </ParticipantTileWrapper>
          </ContentInNumbers>
        ) : (
          <ContentInNumbers id="ContentInNumbers" data-testid="contentInNumbers">
            {t('viewSurvey.participants.listHead.participants')}:{' '}
            <TotalItems id="TotalItems" data-testid="TotalItems">
              {metadata.totalItemsCount}
            </TotalItems>
          </ContentInNumbers>
        )}

        <SearchBoxCont id="SearchBoxCont">
          <SearchBox
            searchPhrase={searchTable}
            placeholder={t('viewSurvey.participants.listHead.search')}
            isIcon={true}
            filterName="search"
            isButtonAvailable={false}
          />
          {/*<ImportButton onClick={() => setImportModalActive(true)} id="InportButton">
            <ImportParticipantIcon id="ImportParticipantIcon" />
            {t('viewSurvey.participants.listHead.importParticipants')}
          </ImportButton>*/}
          <AddButton onClick={() => setModalActive(true)} id="AddButton">
            <AddParticipantIcon id="AddParticipantIcon" />
            {t('viewSurvey.participants.listHead.addParticipant')}
          </AddButton>
        </SearchBoxCont>
      </PageHeader>
      <ParticipantsTable
        deleteItem={handleDeleteModal}
        editItem={(item: IParticipantsItem) => handleModal(true, item)}
        items={items}
        pagingInfo={metadata}
        applyFilters={applyFilters}
        selectedLanguages={language?.split(',').map(lang => getLang(lang)) || []}
        surveyType={surveyType}
      />
      <ConfirmSurveyQuitModal
        active={blocker.state === 'blocked'}
        onClose={handleBlockerCancel}
        onConfirm={handleBlockerConfirm}
        title={t('viewSurvey.participants.modals.navigationBlocker.title')}
        note={t('viewSurvey.participants.modals.navigationBlocker.note')}
        question={t('viewSurvey.participants.modals.navigationBlocker.question')}
        cancelLabel={t('viewSurvey.participants.modals.navigationBlocker.actions.cancel')}
        confirmLabel={t('viewSurvey.participants.modals.navigationBlocker.actions.confirm')}
      />
    </ParticipantsCont>
  );
};

export default Participants;
